import { act, fireEvent, screen, waitFor, within } from '@testing-library/react'
import { inspectionAreaSchema } from 'features/libra/inspectionPlanning/planningTypes'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { zoneParamsSchema } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { expect, it } from 'vitest'

import {
  annotationBounds,
  annotationShape,
  areaCard,
  areaTitleField,
  expectDrawingArmed,
  expectDrawingIdle,
  fieldValues,
  getIntegration,
  historicalArea,
  loadEditorSurface,
  loadMeasuredImage,
  renderOwner,
  resizeEditorViewport,
  sourceZone,
} from './NiiInspectionAreas.test-support'
import {
  draw,
  finishAnnotationDrag,
  startAnnotationDrag,
} from './zoneAnalysis/testing/niiEditorTestUtils'

const integration = getIntegration()

const selectSourceBox = async (label: string) => {
  fireEvent.mouseDown(
    screen.getByRole('combobox', { name: 'Source drawing box' })
  )
  fireEvent.click(
    await screen.findByText(label, {
      selector: '.ant-select-item-option-content',
    })
  )
}

const cancelCurrentDrawing = () => {
  const cancelButton = screen.getAllByRole('button', {
    name: 'Cancel drawing',
  })[0]
  if (!cancelButton) throw new Error('Expected the drawing cancel control')
  fireEvent.click(cancelButton)
}

const expectAnnotationBounds = (
  regionId: string,
  expected: ReturnType<typeof annotationBounds>
) => {
  const actual = annotationBounds(regionId)
  expect(actual.x_start).toBeCloseTo(expected.x_start)
  expect(actual.y_start).toBeCloseTo(expected.y_start)
  expect(actual.x_end).toBeCloseTo(expected.x_end)
  expect(actual.y_end).toBeCloseTo(expected.y_end)
}

it('requires a numbered source-box choice, cancels it after capture changes, then draws inside the chosen box', async () => {
  const splitZone = zoneParamsSchema.parse({
    ...sourceZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 900, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  integration.fieldValues = {
    ...fieldValues([]),
    source_zones: wrapFieldValue([splitZone]),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  let surface = loadEditorSurface()
  expect(annotationShape('nii-source-same-0')).toBeTruthy()
  expect(annotationShape('nii-source-same-1')).toBeTruthy()

  const addArea = screen.getByRole('button', { name: 'Add optional area' })
  expect(addArea.hasAttribute('disabled')).toBe(true)
  await selectSourceBox('Shell box 2')
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('false')
  expect(
    annotationShape('nii-source-same-1').getAttribute('aria-pressed')
  ).toBe('true')
  expect(addArea.hasAttribute('disabled')).toBe(false)
  fireEvent.click(addArea)
  expectDrawingArmed()

  const changedZone = zoneParamsSchema.parse({
    ...splitZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 920, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  integration.fieldValues = {
    ...integration.fieldValues,
    source_zones: wrapFieldValue([changedZone]),
  }
  act(() => integration.notify?.())
  expectDrawingIdle()
  expect(addArea.hasAttribute('disabled')).toBe(true)
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('false')
  expect(
    annotationShape('nii-source-same-1').getAttribute('aria-pressed')
  ).toBe('false')
  expect(
    inspectionAreaSchema
      .array()
      .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  ).toHaveLength(0)

  await selectSourceBox('Shell box 2')
  surface = loadEditorSurface()
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('false')
  expect(
    annotationShape('nii-source-same-1').getAttribute('aria-pressed')
  ).toBe('true')
  fireEvent.click(addArea)
  expectDrawingArmed()
  draw(surface, { x: 250, y: 100 }, { x: 300, y: 150 })
  await waitFor(() => expectDrawingIdle())
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })
  const saved = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved).toHaveLength(1)
  expect(saved[0]).toMatchObject({
    sourceZoneId: 'same',
    zone_reference_image_id: 'image-a',
    drawing_bounds: {
      x_start: 960,
      y_start: 320,
      x_end: 1160,
      y_end: 520,
    },
  })
})

it('uses a source-box overlay choice for both highlight and draw containment', async () => {
  const splitZone = zoneParamsSchema.parse({
    ...sourceZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 900, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  integration.fieldValues = {
    ...fieldValues([]),
    source_zones: wrapFieldValue([splitZone]),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  const surface = loadEditorSurface()

  fireEvent.focus(annotationShape('nii-source-same-1'))
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('false')
  expect(
    annotationShape('nii-source-same-1').getAttribute('aria-pressed')
  ).toBe('true')
  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  expectDrawingArmed()
  draw(surface, { x: 250, y: 100 }, { x: 300, y: 150 })
  await waitFor(() => expectDrawingIdle())
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })

  const [saved] = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved).toMatchObject({
    sourceZoneId: 'same',
    drawing_bounds: {
      x_start: 960,
      y_start: 320,
      x_end: 1160,
      y_end: 520,
    },
  })
})

it('honors explicit Box 2 containment when usable source boxes overlap', async () => {
  const overlappingZone = zoneParamsSchema.parse({
    ...sourceZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 1000, y_end: 700 },
      { x_start: 500, y_start: 100, x_end: 1400, y_end: 700 },
    ],
  })
  integration.fieldValues = {
    ...fieldValues([]),
    source_zones: wrapFieldValue([overlappingZone]),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  const surface = loadEditorSurface()
  await selectSourceBox('Shell box 2')
  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  draw(surface, { x: 250, y: 100 }, { x: 280, y: 130 })
  await waitFor(() => expectDrawingIdle())
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })

  const [saved] = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved?.drawing_bounds?.x_start).toBeCloseTo(960)
  expect(saved?.drawing_bounds?.y_start).toBeCloseTo(320)
  expect(saved?.drawing_bounds?.x_end).toBeCloseTo(1080)
  expect(saved?.drawing_bounds?.y_end).toBeCloseTo(440)
})

it('uses the overlay source zone when choosing a box from another zone on the same image', async () => {
  const otherZone = zoneParamsSchema.parse({
    ...sourceZone,
    id: 'other',
    title: 'Other',
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 900, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  integration.fieldValues = {
    ...fieldValues([]),
    source_zones: wrapFieldValue([sourceZone, otherZone]),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  const surface = loadEditorSurface()

  fireEvent.focus(annotationShape('nii-source-other-1'))
  expect(
    annotationShape('nii-source-other-1').getAttribute('aria-pressed')
  ).toBe('true')
  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  draw(surface, { x: 250, y: 100 }, { x: 300, y: 150 })
  await waitFor(() => expectDrawingIdle())
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })

  const [saved] = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved?.sourceZoneId).toBe('other')
})

it('keeps an existing area selected while Box 2 redraw can be canceled or replace it', async () => {
  const splitZone = zoneParamsSchema.parse({
    ...sourceZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 900, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  const area = inspectionAreaSchema.parse({
    ...historicalArea,
    id: 'preserved',
    sourceZoneId: 'same',
    title: 'Preserved area',
    zone_reference_image_id: 'image-a',
    drawing_bounds: { x_start: 100, y_start: 100, x_end: 300, y_end: 300 },
  })
  integration.fieldValues = {
    ...fieldValues([area]),
    source_zones: wrapFieldValue([splitZone]),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  const surface = loadEditorSurface()
  const card = areaCard('Preserved area')
  fireEvent.click(card)
  await selectSourceBox('Shell box 2')

  expect(card.getAttribute('aria-current')).toBe('true')
  expect(
    annotationShape('nii-area-preserved').getAttribute('aria-pressed')
  ).toBe('true')
  fireEvent.click(within(card).getByRole('button', { name: 'Redraw' }))
  expectDrawingArmed()
  cancelCurrentDrawing()
  await waitFor(() => expectDrawingIdle())
  expectAnnotationBounds('nii-area-preserved', {
    x_start: 100,
    y_start: 100,
    x_end: 300,
    y_end: 300,
  })
  expect(card.getAttribute('aria-current')).toBe('true')

  fireEvent.click(within(card).getByRole('button', { name: 'Redraw' }))
  expectDrawingArmed()
  draw(surface, { x: 250, y: 100 }, { x: 300, y: 150 })
  await waitFor(() => expectDrawingIdle())
  expect(annotationShape('nii-area-preserved')).toBeTruthy()
  expectAnnotationBounds('nii-area-preserved', {
    x_start: 960,
    y_start: 320,
    x_end: 1160,
    y_end: 520,
  })
  await act(async () => {
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))
  })

  const [saved] = inspectionAreaSchema
    .array()
    .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  expect(saved).toMatchObject({
    id: 'preserved',
    title: 'Preserved area',
    methodAssignments: area.methodAssignments,
    drawing_bounds: {
      x_start: 960,
      y_start: 320,
      x_end: 1160,
      y_end: 520,
    },
  })
})

it('invalidates a numbered box choice when the captured assessment changes with identical geometry', async () => {
  const splitZone = zoneParamsSchema.parse({
    ...sourceZone,
    drawing_bounds: [
      { x_start: 0, y_start: 0, x_end: 500, y_end: 400 },
      { x_start: 900, y_start: 300, x_end: 1400, y_end: 700 },
    ],
  })
  integration.globalContext = {
    ...integration.globalContext,
    planningSourceCapture: {
      sourceField: 'source_assessment',
      sourceZonesField: 'source_zones',
      copyFields: {},
      sourceContextField: 'source_context',
    },
  }
  integration.fieldValues = {
    ...fieldValues([]),
    source_zones: wrapFieldValue([splitZone]),
    source_assessment: wrapFieldValue({
      ticketId: 'source-ticket',
      title: 'Assessment',
      version: 1,
      workflowId: 'source-workflow',
      capturedAt: '2026-11-01T06:00:00.000Z',
    }),
  }
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  const addArea = screen.getByRole('button', { name: 'Add optional area' })
  await selectSourceBox('Shell box 2')
  fireEvent.click(addArea)
  expectDrawingArmed()

  integration.fieldValues = {
    ...integration.fieldValues,
    source_assessment: wrapFieldValue({
      ticketId: 'source-ticket',
      title: 'Assessment',
      version: 2,
      workflowId: 'source-workflow',
      capturedAt: '2026-11-01T06:00:00.000Z',
    }),
  }
  act(() => integration.notify?.())
  expectDrawingIdle()
  expect(addArea.hasAttribute('disabled')).toBe(true)
  expect(
    inspectionAreaSchema
      .array()
      .parse(unwrapFieldValue(integration.access.fieldValues.inspection_areas))
  ).toHaveLength(0)
})

it('keeps Add armed across metadata edits and completes the draw after resize', () => {
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  loadEditorSurface()
  const sourceShape = document.querySelector(
    'svg[data-annotation-id="nii-source-same-0"] [data-annotation-shape="true"]'
  )
  expect(sourceShape).toBeTruthy()

  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  expectDrawingArmed()
  expect(
    document.querySelector('[data-region-id="nii-source-same-0"]')
  ).toBeTruthy()
  const title = areaTitleField(areaCard('Saved same'), 'same')
  fireEvent.click(title)
  fireEvent.focus(title)
  fireEvent.change(title, { target: { value: 'Saved same edited' } })
  expectDrawingArmed()
  const instructions = areaCard(
    'Saved same edited'
  ).querySelector<HTMLTextAreaElement>('textarea#ut-same-instructions')
  if (!instructions) throw new Error('Expected method instructions field')
  fireEvent.click(instructions)
  fireEvent.focus(instructions)
  fireEvent.change(instructions, {
    target: { value: 'Scan the edited band' },
  })
  expectDrawingArmed()
  const surface = screen.getByRole('application')
  resizeEditorViewport(surface, 520, 260)
  const pageFrame = document.querySelector<HTMLElement>(
    '[data-markup-page-frame]'
  )
  if (!pageFrame) throw new Error('Expected the resized markup page frame')
  expect(getComputedStyle(pageFrame).width).toBe('520px')
  const existingAreaIds = new Set(
    Array.from(
      document.querySelectorAll<SVGSVGElement>(
        'svg[data-annotation-id^="nii-area-"]'
      )
    ).map((svg) => svg.getAttribute('data-annotation-id'))
  )
  draw(surface, { x: 140, y: 85 }, { x: 192, y: 150 })
  const drawnArea = Array.from(
    document.querySelectorAll<SVGSVGElement>(
      'svg[data-annotation-id^="nii-area-"]'
    )
  ).find((svg) => !existingAreaIds.has(svg.getAttribute('data-annotation-id')))
  const drawnAreaId = drawnArea?.getAttribute('data-annotation-id')
  if (!drawnAreaId) throw new Error('Expected the newly drawn area outline')
  expectAnnotationBounds(drawnAreaId, {
    x_start: 400,
    y_start: 200,
    x_end: 560,
    y_end: 400,
  })
  expect(areaCard('Shell · Area 3')).toBeTruthy()

  expectDrawingIdle()
  fireEvent.click(screen.getByRole('button', { name: 'Add optional area' }))
  expectDrawingArmed()
  fireEvent.click(screen.getByRole('button', { name: 'Reset' }))
  expectDrawingIdle()
  expect(areaTitleField(areaCard('Saved same'), 'same').value).toBe(
    'Saved same'
  )
  expect(screen.queryByText('Shell · Area 3')).toBeNull()
}, 15_000)

it('rearms Redraw after Reset restores the editor, then Reset cancels it', () => {
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  loadEditorSurface()

  let card = areaCard('Saved same')
  fireEvent.change(areaTitleField(card, 'same'), {
    target: { value: 'Reset before redraw' },
  })
  fireEvent.click(screen.getByRole('button', { name: 'Reset' }))
  expectDrawingIdle()
  loadEditorSurface()

  card = areaCard('Saved same')
  fireEvent.change(areaTitleField(card, 'same'), {
    target: { value: 'Redraw reset edit' },
  })
  fireEvent.click(within(card).getByRole('button', { name: 'Redraw' }))
  expectDrawingArmed()
  fireEvent.click(screen.getByRole('button', { name: 'Reset' }))
  expectDrawingIdle()
})

it('Reset cancels active annotation movement and keeps the locked source focused', () => {
  renderOwner()
  fireEvent.click(screen.getByRole('button', { name: 'Open NII drawing' }))
  loadMeasuredImage('/image-a.png')
  loadEditorSurface()

  const peer = areaCard('Saved peer')
  fireEvent.change(areaTitleField(peer, 'peer'), {
    target: { value: 'Peer reset edit' },
  })
  fireEvent.click(within(peer).getByRole('button', { name: 'Show' }))
  loadEditorSurface()
  const editable = document.querySelector(
    'svg[data-annotation-id="nii-area-peer"] [data-annotation-shape="true"]'
  )
  if (!editable) throw new Error('Expected peer area outline')
  const before = annotationBounds('nii-area-peer')
  startAnnotationDrag(editable, { x: 190, y: 50 }, { x: 210, y: 60 })
  expect(annotationBounds('nii-area-peer')).not.toEqual(before)
  fireEvent.click(screen.getByRole('button', { name: 'Reset' }))
  expectDrawingIdle()
  finishAnnotationDrag({ x: 210, y: 60 })
  expect(annotationBounds('nii-area-peer')).toEqual(before)

  const sourceAfterReset = document.querySelector(
    'svg[data-annotation-id="nii-source-same-0"] [data-annotation-shape="true"]'
  )
  if (!sourceAfterReset)
    throw new Error('Expected the source outline after Cancel')
  fireEvent.focus(sourceAfterReset)
  expect(
    annotationShape('nii-source-same-0').getAttribute('aria-pressed')
  ).toBe('true')
})
